import { useForm } from "react-hook-form";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useState } from "react";


const AddCarForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [thumbnail, setThumbnail] = useState(null); 
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(
      (file) => file.type.match("image.*") && file.size <= 5 * 1024 * 1024
    );
    setFiles(validFiles);
  };
  

  const onSubmit = async (data) => {
    if (files.length === 0) {
      alert("Please upload at least one image");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();

    // Append form data
    Object.entries(data).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });

    formData.append("thumbnail", thumbnail);

    // Append files
    files.forEach((file) => {
      formData.append("carImages", file);
    });

    try {
      await axios.post("https://automotive-backend-production.up.railway.app/inventory", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      alert("Car added successfully!");
      navigate("/");
    } catch (error) {
      console.error("Upload error:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Add New Car to Inventory</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="row g-3">
        {/* Basic Information */}
        <div className="col-md-6">
          <label className="form-label">Name*</label>
          <input
            {...register("Name", { required: "Name is required" })}
            className={`form-control ${errors.Name ? "is-invalid" : ""}`}
          />
          {errors.Name && (
            <div className="invalid-feedback">{errors.Name.message}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Make*</label>
          <input
            {...register("Make", { required: "Make is required" })}
            className={`form-control ${errors.Make ? "is-invalid" : ""}`}
          />
          {errors.Make && (
            <div className="invalid-feedback">{errors.Make.message}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Model*</label>
          <input
            {...register("Model", { required: "Model is required" })}
            className={`form-control ${errors.Model ? "is-invalid" : ""}`}
          />
          {errors.Model && (
            <div className="invalid-feedback">{errors.Model.message}</div>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Year*</label>
          <input
            type="number"
            {...register("Year", {
              required: "Year is required",
              min: { value: 1900, message: "Year too old" },
              max: {
                value: new Date().getFullYear() + 1,
                message: "Invalid future year",
              },
            })}
            className={`form-control ${errors.Year ? "is-invalid" : ""}`}
          />
          {errors.Year && (
            <div className="invalid-feedback">{errors.Year.message}</div>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Chassis</label>
          <input
            {...register("Chassis")}
            className="form-control"
            placeholder="Chassis"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Price ($)*</label>
          <input
            type="number"
            step="0.01"
            {...register("Price", {
              required: "Price is required",
              min: { value: 0, message: "Price cannot be negative" },
            })}
            className={`form-control ${errors.Price ? "is-invalid" : ""}`}
          />
          {errors.Price && (
            <div className="invalid-feedback">{errors.Price.message}</div>
          )}
        </div>

        {/* Specifications */}
        <div className="col-md-3">
          <label className="form-label">Mileage</label>
          <input
            type="number"
            {...register("Mileage", { min: 0 })}
            className="form-control"
          />
        </div>

        {/* Seats */}
        <div className="col-md-3">
          <label className="form-label">Seats</label>
          <input
            type="number"
            {...register("Seats", { min: 0 })}
            className="form-control"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Steering</label>
          <select {...register("Steering")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="Right Hand Drive">Right Hand Drive</option>
            <option value="Left Hand Drive">Left Hand Drive</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Color</label>
          <input {...register("Color")} className="form-control" />
        </div>

        <div className="col-md-3">
          <label className="form-label">Transmission</label>
          <select {...register("Transmission")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="Automatic">Automatic</option>
            <option value="Manual">Manual</option>
            <option value="CVT">CVT</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Fuel Type</label>
          <select {...register("FuelType")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="Gasoline">Gasoline</option>
            <option value="Petrol">Petrol</option>
            <option value="Diesel">Diesel</option>
            <option value="Electric">Electric</option>
            <option value="Hybrid">Hybrid</option>
            <option value="Plug-In Hybrid">Plug-In Hybrid</option>
            <option value="Hybrid Petrol">Hybrid Petrol</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Drive Type</label>
          <select {...register("DriveType")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="2-Wheel Drive">2-Wheel Drive</option>
            <option value="4-Wheel Drive">4-Wheel Drive</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Condition</label>
          <select {...register("Condition")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="New">New</option>
            <option value="Used">Used</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Engine Size</label>
          <input
            type="text"
            {...register("EngineSize")}
            className="form-control"
            placeholder="e.g., 2,200.0L"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Category</label>
          <select {...register("Category")} className="form-select">
            <option value="" disabled>Select</option>
            <option value="Sedan">Sedan</option>
            <option value="SUV">SUV</option>
            <option value="Hatchback">Hatchback</option>
            <option value="Van">Van</option>
            <option value="Mini Van">Mini Van</option>
            <option value="Wagon">Wagon</option>
            <option value="Station Wagon">Station Wagon</option>
            <option value="Pickup">Pickup</option>
            <option value="Coupe">Coupe</option>
            <option value="Truck">Truck</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Doors</label>
          <input
            type="number"
            {...register("Doors", {
              min: { value: 1, message: "Must be at least 1 door" },
              max: { value: 8, message: "Too many doors?" },
            })}
            className={`form-control ${errors.Doors ? "is-invalid" : ""}`}
          />
          {errors.Doors && (
            <div className="invalid-feedback">{errors.Doors.message}</div>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Country</label>
          <input
            {...register("Country")}
            className="form-control"
            placeholder="e.g., Japan"
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Features</label>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="ABS"
              {...register("Feature")}
              id="feature-abs"
            />
            <label className="form-check-label" htmlFor="feature-abs">
              ABS
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Adaptive headlights"
              {...register("Feature")}
              id="feature-headlights"
            />
            <label className="form-check-label" htmlFor="feature-headlights">
              Adaptive headlights
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Bluetooth"
              {...register("Feature")}
              id="feature-bluetooth"
            />
            <label className="form-check-label" htmlFor="feature-bluetooth">
              Bluetooth
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="ACC"
              {...register("Feature")}
              id="feature-acc"
            />
            <label className="form-check-label" htmlFor="feature-acc">
              ACC
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Air Conditioner"
              {...register("Feature")}
              id="feature-airconditioner"
            />
            <label
              className="form-check-label"
              htmlFor="feature-airconditioner"
            >
              Air Conditioner
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Audio Player"
              {...register("Feature")}
              id="feature-audioplayer"
            />
            <label className="form-check-label" htmlFor="feature-audioplayer">
              Audio Player
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Both Power Slide Door"
              {...register("Feature")}
              id="feature-bothpowerslidedoor"
            />
            <label
              className="form-check-label"
              htmlFor="feature-bothpowerslidedoor"
            >
              Both Power Slide Door
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Cooler"
              {...register("Feature")}
              id="feature-cooler"
            />
            <label className="form-check-label" htmlFor="feature-cooler">
              Cooler
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Cruise Control"
              {...register("Feature")}
              id="feature-cruisecontrol"
            />
            <label className="form-check-label" htmlFor="feature-cruisecontrol">
              Cruise Control
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Double Air Conditioner"
              {...register("Feature")}
              id="feature-doubleairconditioner"
            />
            <label
              className="form-check-label"
              htmlFor="feature-doubleairconditioner"
            >
              Double Air Conditioner
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Left Side Power Slide Door"
              {...register("Feature")}
              id="feature-leftsidepowerslidedoor"
            />
            <label
              className="form-check-label"
              htmlFor="feature-leftsidepowerslidedoor"
            >
              Left Side Power Slide Door
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Navigation System"
              {...register("Feature")}
              id="feature-navigationsystem"
            />
            <label
              className="form-check-label"
              htmlFor="feature-navigationsystem"
            >
              Navigation System
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Paddle Shift"
              {...register("Feature")}
              id="feature-paddleshift"
            />
            <label className="form-check-label" htmlFor="feature-paddleshift">
              Paddle Shift
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Back Door"
              {...register("Feature")}
              id="feature-powerbackdoor"
            />
            <label className="form-check-label" htmlFor="feature-powerbackdoor">
              Power Back Door
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Seat"
              {...register("Feature")}
              id="feature-powerseat"
            />
            <label className="form-check-label" htmlFor="feature-powerseat">
              Power Seat
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Steering"
              {...register("Feature")}
              id="feature-powersteering"
            />
            <label className="form-check-label" htmlFor="feature-powersteering">
              Power Steering
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Window All"
              {...register("Feature")}
              id="feature-powerwindowall"
            />
            <label
              className="form-check-label"
              htmlFor="feature-powerwindowall"
            >
              Power Window All
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Window Driver"
              {...register("Feature")}
              id="feature-powerwindowdriver"
            />
            <label
              className="form-check-label"
              htmlFor="feature-powerwindowdriver"
            >
              Power Window Driver
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Power Window Front"
              {...register("Feature")}
              id="feature-powerwindowfront"
            />
            <label
              className="form-check-label"
              htmlFor="feature-powerwindowfront"
            >
              Power Window Front
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Push Start"
              {...register("Feature")}
              id="feature-pushstart"
            />
            <label className="form-check-label" htmlFor="feature-pushstart">
              Push Start
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Steering Switch"
              {...register("Feature")}
              id="feature-steeringswitch"
            />
            <label
              className="form-check-label"
              htmlFor="feature-steeringswitch"
            >
              Steering Switch
            </label>
          </div>
          {/* Dress Up */}
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Aero Front"
              {...register("Feature")}
              id="feature-aerofront"
            />
            <label className="form-check-label" htmlFor="feature-aerofront">
              Aero Front
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Aero Rear"
              {...register("Feature")}
              id="feature-aerorear"
            />
            <label className="form-check-label" htmlFor="feature-aerorear">
              Aero Rear
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Aero Side"
              {...register("Feature")}
              id="feature-aeroside"
            />
            <label className="form-check-label" htmlFor="feature-aeroside">
              Aero Side
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Alloy Wheels"
              {...register("Feature")}
              id="feature-alloywheels"
            />
            <label className="form-check-label" htmlFor="feature-alloywheels">
              Alloy Wheels
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Grill Guard"
              {...register("Feature")}
              id="feature-grillguard"
            />
            <label className="form-check-label" htmlFor="feature-grillguard">
              Grill Guard
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Rear Spoiler"
              {...register("Feature")}
              id="feature-rearspoiler"
            />
            <label className="form-check-label" htmlFor="feature-rearspoiler">
              Rear Spoiler
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Wood Combination Steering"
              {...register("Feature")}
              id="feature-woodsteering"
            />
            <label className="form-check-label" htmlFor="feature-woodsteering">
              Wood Combination Steering
            </label>
          </div>

          {/* Exterior */}
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Carrier Base"
              {...register("Feature")}
              id="feature-carrierbase"
            />
            <label className="form-check-label" htmlFor="feature-carrierbase">
              Carrier Base
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Roof Box"
              {...register("Feature")}
              id="feature-roofbox"
            />
            <label className="form-check-label" htmlFor="feature-roofbox">
              Roof Box
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Roof Rack"
              {...register("Feature")}
              id="feature-roofrack"
            />
            <label className="form-check-label" htmlFor="feature-roofrack">
              Roof Rack
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Roof Rails"
              {...register("Feature")}
              id="feature-roofrails"
            />
            <label className="form-check-label" htmlFor="feature-roofrails">
              Roof Rails
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Sun Roof"
              {...register("Feature")}
              id="feature-sunroof"
            />
            <label className="form-check-label" htmlFor="feature-sunroof">
              Sun Roof
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Double Sun Roof"
              {...register("Feature")}
              id="feature-doublesunroof"
            />
            <label className="form-check-label" htmlFor="feature-doublesunroof">
              Double Sun Roof
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Glass Roof"
              {...register("Feature")}
              id="feature-glassroof"
            />
            <label className="form-check-label" htmlFor="feature-glassroof">
              Glass Roof
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Fog Light"
              {...register("Feature")}
              id="feature-foglight"
            />
            <label className="form-check-label" htmlFor="feature-foglight">
              Fog Light
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="HID Light"
              {...register("Feature")}
              id="feature-hidlight"
            />
            <label className="form-check-label" htmlFor="feature-hidlight">
              HID Light
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="LED Light"
              {...register("Feature")}
              id="feature-ledlight"
            />
            <label className="form-check-label" htmlFor="feature-ledlight">
              LED Light
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="High Roof"
              {...register("Feature")}
              id="feature-highroof"
            />
            <label className="form-check-label" htmlFor="feature-highroof">
              High Roof
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="New Shape"
              {...register("Feature")}
              id="feature-newshape"
            />
            <label className="form-check-label" htmlFor="feature-newshape">
              New Shape
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Slide Glass"
              {...register("Feature")}
              id="feature-slideglass"
            />
            <label className="form-check-label" htmlFor="feature-slideglass">
              Slide Glass
            </label>
          </div>

          {/* Safety */}
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="ABS"
              {...register("Feature")}
              id="feature-abs"
            />
            <label className="form-check-label" htmlFor="feature-abs">
              ABS
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Air Bag"
              {...register("Feature")}
              id="feature-airbag"
            />
            <label className="form-check-label" htmlFor="feature-airbag">
              Air Bag
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Around View Monitor"
              {...register("Feature")}
              id="feature-aroundview"
            />
            <label className="form-check-label" htmlFor="feature-aroundview">
              Around View Monitor
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Back Camera"
              {...register("Feature")}
              id="feature-backcamera"
            />
            <label className="form-check-label" htmlFor="feature-backcamera">
              Back Camera
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Front Camera"
              {...register("Feature")}
              id="feature-frontcamera"
            />
            <label className="form-check-label" htmlFor="feature-frontcamera">
              Front Camera
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Side Camera"
              {...register("Feature")}
              id="feature-sidecamera"
            />
            <label className="form-check-label" htmlFor="feature-sidecamera">
              Side Camera
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Corner Sensor"
              {...register("Feature")}
              id="feature-cornersensor"
            />
            <label className="form-check-label" htmlFor="feature-cornersensor">
              Corner Sensor
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="ESC"
              {...register("Feature")}
              id="feature-esc"
            />
            <label className="form-check-label" htmlFor="feature-esc">
              ESC
            </label>
          </div>

          {/* Other */}
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Auto Door"
              {...register("Feature")}
              id="feature-autodoor"
            />
            <label className="form-check-label" htmlFor="feature-autodoor">
              Auto Door
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Back Tire"
              {...register("Feature")}
              id="feature-backtire"
            />
            <label className="form-check-label" htmlFor="feature-backtire">
              Back Tire
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Just Low"
              {...register("Feature")}
              id="feature-justlow"
            />
            <label className="form-check-label" htmlFor="feature-justlow">
              Just Low
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Half Leather Seat"
              {...register("Feature")}
              id="feature-halfleatherseat"
            />
            <label
              className="form-check-label"
              htmlFor="feature-halfleatherseat"
            >
              Half Leather Seat
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Leather Seat"
              {...register("Feature")}
              id="feature-leatherseat"
            />
            <label className="form-check-label" htmlFor="feature-leatherseat">
              Leather Seat
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Spare tyre"
              {...register("Feature")}
              id="feature-sametire"
            />
            <label className="form-check-label" htmlFor="feature-sametire">
              Spare tyre
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Side Lift Up Seat"
              {...register("Feature")}
              id="feature-sideliftupseat"
            />
            <label
              className="form-check-label"
              htmlFor="feature-sideliftupseat"
            >
              Side Lift Up Seat
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              value="Sloper"
              {...register("Feature")}
              id="feature-sloper"
            />
            <label className="form-check-label" htmlFor="feature-sloper">
              Sloper
            </label>
          </div>

          {/* Add more checkboxes as needed */}
        </div>

          {/* Thumbnail Upload */}
          <div className="col-12">
            <label className="form-label">Upload Thumbnail (Max 5MB)*</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnail(e.target.files[0])}
              className="form-control"
              required
            />
            <small className="text-muted">JPEG, PNG only (1 image)</small>
          </div>

        {/* Image Upload */}
        <div className="col-12">
          <label className="form-label">Upload Images (Max 5MB each)*</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="form-control"
            required
          />
          <small className="text-muted">JPEG, PNG only (up to 30 images)</small>

          {files.length > 0 && (
            <div className="mt-3">
              <h6>Selected Files:</h6>
              <ul className="list-group mb-3">
                {files.map((file, index) => (
                  <li
                    key={index}
                    className="list-group-item d-flex justify-content-between"
                  >
                    {file.name}
                    <span className="badge bg-primary rounded-pill">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </li>
                ))}
              </ul>

              <div className="progress mb-3">
                <div
                  className="progress-bar progress-bar-striped"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-12 mt-4">
          <button
            type="submit"
            className="btn btn-primary me-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Saving...
              </>
            ) : (
              "Save Car"
            )}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/")}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCarForm;
